import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react'
import { z } from 'zod'
import api from '@/admin/lib/api'
import { extractApiErrorMessage } from '@/shared/lib/api-error'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

/**
 * Self-service page for a partner whose organisation was migrated onto raven as
 * inert data: the company exists here, but no login does.
 *
 * Deliberately NOT a password reset. Those partners have no account, and the
 * platform is configured to send nothing for an unknown address while still
 * saying "check your email", so a reset link leaves them waiting forever with no
 * error anywhere. This asks for the address we already hold for them and sends
 * the ordinary organisation invitation instead.
 */

type FormValues = { email: string }

type Location = { uuid: string; name: string }

type RequestOutcome =
  | { kind: 'idle' }
  | { kind: 'sent'; organisation?: string }
  | { kind: 'already_active' }
  | { kind: 'choose'; locations: Location[] }
  | { kind: 'error'; message: string }

export function AccountMigrationPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [outcome, setOutcome] = useState<RequestOutcome>({ kind: 'idle' })
  const [email, setEmail] = useState('')

  async function submitRequest(address: string, organisationUuid?: string) {
    setLoading(true)
    try {
      // The shared client, not fetch: its response interceptor unwraps the
      // `{status, message, result}` envelope, so `data` IS the payload. Reading
      // it by hand is how the earlier version looked for a `data` key that the
      // backend never sends (it is `result`), which silently disabled the
      // already-active and choose-a-location branches below.
      const { data } = await api.post(
        '/public/account-migration/request/',
        organisationUuid
          ? { email: address, organisation_uuid: organisationUuid }
          : { email: address },
      )
      const payload = (data ?? {}) as Record<string, unknown>

      if (payload.already_active) {
        setOutcome({ kind: 'already_active' })
        return
      }
      if (payload.requires_choice) {
        setOutcome({
          kind: 'choose',
          locations: (payload.organisations as Location[]) ?? [],
        })
        return
      }
      setOutcome({
        kind: 'sent',
        organisation:
          typeof payload.organisation === 'string'
            ? payload.organisation
            : undefined,
      })
    } catch (error) {
      // A 404 here is the deliberate "we do not recognise that address"
      // response, so the server's own message is the useful one. Only fall back
      // when there is no response at all (network/CORS).
      setOutcome({
        kind: 'error',
        message:
          extractApiErrorMessage(error) ||
          t('accountMigration.errorGeneric'),
      })
    } finally {
      setLoading(false)
    }
  }

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'email',
        name: 'email',
        label: t('accountMigration.emailLabel'),
        placeholder: t('accountMigration.emailPlaceholder'),
        validationSchema: z
          .string()
          .min(1, t('accountMigration.emailInvalid'))
          .email(t('accountMigration.emailInvalid')),
      },
    ],
    [t],
  )

  const { Form } = useForm(
    { name: 'account-migration-form', submit: 'footer', fields },
    async (rawValues) => {
      const values = rawValues as unknown as FormValues
      setEmail(values.email)
      await submitRequest(values.email)
    },
    [],
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-[400px]">
        <div className="mb-8">
          <Logo size="md" />
        </div>

        {outcome.kind === 'sent' ? (
          <div className="text-center">
            <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
            <h2 className="mb-2 text-[20px] font-display text-[var(--ink)]">
              {t('accountMigration.sentTitle')}
            </h2>
            <p className="clara-small mb-6">
              {outcome.organisation
                ? t('accountMigration.sentDescriptionOrg', {
                    email,
                    organisation: outcome.organisation,
                  })
                : t('accountMigration.sentDescription', { email })}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="cursor-pointer"
            >
              {t('accountMigration.backToSignIn')}
            </Button>
          </div>
        ) : outcome.kind === 'already_active' ? (
          <div className="text-center">
            <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
            <h2 className="mb-2 text-[20px] font-display text-[var(--ink)]">
              {t('accountMigration.alreadyTitle')}
            </h2>
            <p className="clara-small mb-6">
              {t('accountMigration.alreadyDescription')}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="cursor-pointer"
            >
              {t('accountMigration.goToSignIn')}
            </Button>
          </div>
        ) : outcome.kind === 'choose' ? (
          <div>
            <h2 className="mb-1 text-[28px] leading-[1.1] font-display text-[var(--ink)]">
              {t('accountMigration.chooseTitle')}
            </h2>
            <p className="clara-small mb-6">
              {t('accountMigration.chooseDescription')}
            </p>
            <div className="space-y-2">
              {outcome.locations.map((location) => (
                <Button
                  key={location.uuid}
                  variant="outline"
                  disabled={loading}
                  onClick={() => submitRequest(email, location.uuid)}
                  className="w-full cursor-pointer justify-start"
                >
                  {location.name}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <h2 className="mb-1 text-[28px] leading-[1.1] font-display text-[var(--ink)]">
              {t('accountMigration.title')}
            </h2>
            <p className="clara-small mb-6">
              {t('accountMigration.description')}
            </p>

            <Form className="space-y-4" />

            {outcome.kind === 'error' && (
              <p className="mt-3 text-[12px] text-[var(--status-error)]">
                {outcome.message}
              </p>
            )}

            <Button
              type="submit"
              form="account-migration-form"
              disabled={loading}
              className="mt-4 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              ) : (
                t('accountMigration.sendLink')
              )}
            </Button>

            <Button
              variant="link"
              onClick={() => navigate('/login')}
              className="mt-6 flex items-center gap-1.5 px-0 text-[11px] text-[var(--green)] cursor-pointer"
            >
              <ArrowLeft className="h-3 w-3" />
              {t('accountMigration.backToSignIn')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
